import { cloneDeep, isEqual, omit } from "lodash";

import { PermissionApi } from "lib/api";
import { Permission, User, UserGroup } from "lib/data/model";
import { SingleRecordPermission } from "lib/data/model/security/SingleRecordPermission";

export class PermissionService {

	public static userHasPermission = async (user: User, permission: Permission | SingleRecordPermission): Promise<boolean> => {

		// Check Permission Object
		if (!PermissionService.isPermissionValid(permission)) {

			// Return NOT Permitted
			return false;
		}

		// Check If Permitted
		const isPermitted = await PermissionService.isPermitted(
			user,
			permission,
			(theUser, thePermission) => PermissionApi.isUserPermitted(theUser, thePermission)
		);

		// Return Whether Permitted
		return isPermitted;
	}

	public static userGroupHasPermission = async (group: UserGroup, permission: Permission): Promise<boolean> => {

		// Check Permission Object
		if (!PermissionService.isPermissionValid(permission)) {

			// Return NOT Permitted
			return false;
		}

		// Check If Permitted
		const isPermitted = await PermissionService.isPermitted(
			group,
			permission,
			(theUserGroup, thePermission) => PermissionApi.isUserGroupPermitted(theUserGroup, thePermission)
		);

		// Return Whether Permitted
		return isPermitted;
	}

	public static isPermissionValid = (permission: Permission): boolean => {

		// Check Permission Object
		if ((permission as SingleRecordPermission).recordId !== undefined
			&& permission.recordType === undefined) {

			// Return Valid, Cannot Search For ID-based Permissions Without The Type
			return false;
		}

		// Return Valid
		return true;
	}

	private static isPermitted = async (
		who: any,
		permission: Permission,
		how: (who: any, permission: Permission) => Promise<boolean | undefined>
	): Promise<boolean> => {

		// Initialize Permissions Array
		const permissions: Permission[] = [];

		// Get Previously Saved Permissions From Storage
		const permissionsJson = sessionStorage.getItem(`${who.$type}.Permissions`);

		// Check Stored Permissions
		if (permissionsJson !== null) {

			// Parse Permissions JSON
			const storedPermissions: Permission[] = JSON.parse(permissionsJson);

			// Add All Stored Permissions
			permissions.push(...storedPermissions);
		}

		// Clone Requested Permission
		const requestedPermission = omit(cloneDeep(permission), "__proto__");

		// Search Permissions List
		const storedPermission: Permission | undefined = permissions.find((userPermission) => isEqual(omit(userPermission, "__proto__"), requestedPermission));

		// Check For Found Permission
		if (storedPermission === undefined) {

			// Make Permissions Request
			const isPermitted = await how(who, permission);

			// Check For Permitted
			if (isPermitted !== undefined && isPermitted) {

				// Add New Permissions To Storage
				permissions.push(permission);

				// Serialize Permissions
				const serializedPermissions = JSON.stringify(permissions);

				// Store Permissions
				sessionStorage.setItem(`${who.$type}.Permissions`, serializedPermissions);

				// Return Permitted
				return true;
			}
			else {

				// Return NOT Permitted
				return false;
			}
		}
		else {

			// Return Permitted
			return true;
		}
	}
}
